package com.htmlagent.site.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier

/**
 * The accessible name of a control: `aria-label` and `title` at once.
 *
 * They go together because an icon with no text needs both: the first is heard by screen
 * reader users, the second is seen by whoever hovers the mouse over it.
 */
fun Modifier.labelled(text: String): Modifier = attrsModifier {
    attr("aria-label", text)
    attr("title", text)
}

/**
 * The accessible name without the browser's native tooltip.
 *
 * For controls wrapped in `Tooltip`, which already show that text: with [labelled] both
 * tooltips would overlap.
 */
fun Modifier.named(text: String): Modifier = attrsModifier { attr("aria-label", text) }

/** Marks the control for the section currently being viewed. */
fun Modifier.current(active: Boolean): Modifier =
    if (active) attrsModifier { attr("aria-current", "true") } else this

/**
 * A two-state control: `aria-pressed` makes a screen reader announce "pressed", which
 * otherwise only the button's background would convey.
 */
fun Modifier.pressed(active: Boolean): Modifier =
    attrsModifier { attr("aria-pressed", active.toString()) }

/**
 * Marks the pane that is not on screen while the workspace shows one at a time.
 *
 * It hides with `display: none`, which is what keeps the pane mounted: the conversation keeps its
 * scroll and its draft, and the preview keeps the page the iframe already loaded, so crossing
 * back and forth costs nothing and reloads nothing. `display: none` is also what takes the whole
 * subtree out of the tab order and out of the accessibility tree, so no `inert` is needed here -
 * unlike the closed history panel, which stays visible in the layout.
 *
 * The flag is computed in Kotlin and never written on a wide screen, so the rule that reads this
 * attribute needs no breakpoint of its own and the two can never disagree.
 */
fun Modifier.offPane(off: Boolean): Modifier =
    if (off) attrsModifier { attr(OFF_PANE_ATTR, "true") } else this
